import React, { useCallback, useEffect, useState } from "react";
import { radStudyApi } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Badge, Table, Tabs } from "../../Components/UI/ui";
import { RAD_STATUS_LABEL, MODALITIES, modalityLabel, fmtDateTime, todayStr, addDays } from "../../lib/lab";
import StudyDetail from "./StudyDetail";

// Radiology worklists (radiology doc §5). "Scanner" is the radiographer's
// queue, "Reporting" the radiologist's; both read /labRadiologyStudies.
const VIEWS = [
  { id: "scanner", label: "Scanner", status: "PENDING,ACQUIRED", hint: "Scans to perform and images to upload" },
  { id: "reporting", label: "Reporting", status: "ACQUIRED,DRAFT", hint: "Scans waiting for a report" },
  { id: "signoff", label: "Verified", status: "VERIFIED", hint: "Signed, waiting to be released" },
  { id: "published", label: "Published", status: "PUBLISHED", dated: true },
  { id: "all", label: "All", status: "", dated: true },
];

const RadiologyWorklist = ({ rooms = [] }) => {
  const { toast } = useApp();
  const [view, setView] = useState("scanner");
  const [modality, setModality] = useState("");
  const [q, setQ] = useState("");
  const [startDate, setStartDate] = useState(addDays(todayStr(), -14));
  const [endDate, setEndDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const v = VIEWS.find((x) => x.id === view);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (v.status) params.status = v.status;
      if (v.dated) { params.startDate = startDate; params.endDate = endDate; }
      if (modality) params.modality = modality;
      if (q.trim()) params.q = q.trim();
      const res = await radStudyApi.list(params);
      setRows(Array.isArray(res.data) ? res.data : res.data?.studies || res.data?.data || []);
    } catch (e) { toast.error(e); } finally { setLoading(false); }
  }, [v, modality, q, startDate, endDate, toast]);

  useEffect(() => {
    const h = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(h);
  }, [load, q]);

  const columns = [
    { key: "patientName", title: "Patient", render: (r) => (<><div className="primary">{r.patientName}</div><div className="sub">{r.accession || "no accession yet"}</div></>) },
    { key: "procedure", title: "Scan", render: (r) => (<><div>{r.templateSnapshot?.name || modalityLabel(r.modality)}</div><div className="sub">{modalityLabel(r.modality)}{r.bodyPart ? ` · ${r.bodyPart}` : ""}{r.laterality ? ` · ${r.laterality}` : ""}</div></>) },
    { key: "images", title: "Images", render: (r) => r.dicom?.instanceCount ? `${r.dicom.instanceCount}` : <span className="sub">—</span> },
    { key: "acquiredAt", title: "Scan done", render: (r) => r.acquiredAt ? (<><div>{fmtDateTime(r.acquiredAt)}</div><div className="sub">{r.radiographer?.name || ""}</div></>) : <span className="sub">—</span> },
    { key: "reportedBy", title: "Reported by", render: (r) => r.reportedBy?.name || <span className="sub">—</span> },
    { key: "status", title: "Status", render: (r) => (<span className="lab-row">
        <Badge kind={r.status}>{RAD_STATUS_LABEL[r.status] || r.status}</Badge>
        {r.revision > 1 && <Badge kind="IN_PROCESS">rev {r.revision}</Badge>}
      </span>) },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%", minHeight: 0 }}>
      <Tabs tabs={VIEWS} value={view} onChange={setView} />
      <div className="lab-toolbar">
        <input className="lab-input grow" placeholder="Patient, accession or procedure" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="lab-select" value={modality} onChange={(e) => setModality(e.target.value)}>
          <option value="">All modalities</option>
          {MODALITIES.map((m) => <option key={m} value={m}>{modalityLabel(m)}</option>)}
        </select>
        {v.dated && (
          <>
            <input type="date" className="lab-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="lab-muted">to</span>
            <input type="date" className="lab-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </>
        )}
        {v.hint && <span className="lab-muted" style={{ fontSize: 12 }}>{v.hint}</span>}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Table columns={columns} rows={rows} loading={loading} onRowClick={(r) => setSelected(r._id)} empty="Nothing in this queue" />
      </div>
      <StudyDetail id={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} rooms={rooms} />
    </div>
  );
};

export default RadiologyWorklist;
